import { Badge, Box, Flex, HStack, Text } from '@chakra-ui/react';
import { Accordion, ButtonText, Checkbox } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faEnvelope,
  faUniversity
} from '@fortawesome/pro-regular-svg-icons';
import ReviewerConflictInfo from '../ReviewerConflictInfo';
import { IExternalRecommendation } from './types';
import { OrcidIDIcon } from 'src/components/User/RenderSectionDetails';

const CONFLICT_LABELS: {
  key: keyof NonNullable<IExternalRecommendation['conflicts']>;
  label: string;
}[] = [
  { key: 'affiliation', label: 'Affiliation' },
  { key: 'coauthor', label: 'Co-author' },
  { key: 'external', label: 'External COI' },
  { key: 'referenced', label: 'Referenced articles' },
  { key: 'retracted', label: 'Retracted articles' }
];

interface Props {
  reviewer: IExternalRecommendation;
  providerName: string;
  isSelected: boolean;
  isInvited: boolean;
  expandAll: boolean;
  onChange: (reviewer: IExternalRecommendation) => void;
  onViewDetails: (reviewer: IExternalRecommendation) => void;
}

const ExternalRecommendationCard = ({
  reviewer,
  providerName,
  isSelected,
  isInvited,
  expandAll,
  onChange,
  onViewDetails
}: Props) => {
  const { name, email, affiliation, orcid, score, coi, conflicts } = reviewer;

  // Mirror advanced-search invite disabling: COI that disallows (isAllowed === false)
  // or hard errors disable the checkbox. Warnings alone do not disable.
  const disabled =
    isInvited || coi?.isAllowed === false || (coi?.error?.length ?? 0) > 0;

  const activeConflicts = CONFLICT_LABELS.filter(
    ({ key }) => conflicts?.[key] === true
  );

  const bibliometrics = [
    reviewer.hIndex !== null && reviewer.hIndex !== undefined
      ? `h-Index ${reviewer.hIndex}`
      : null,
    reviewer.citationsCount !== null && reviewer.citationsCount !== undefined
      ? `${reviewer.citationsCount} Citations`
      : null,
    reviewer.worksCount !== null && reviewer.worksCount !== undefined
      ? `${reviewer.worksCount} Articles`
      : null
  ].filter(Boolean);

  return (
    <Accordion
      key={expandAll ? 'expanded' : 'collapsed'}
      chakraAccordionProps={{
        allowMultiple: true,
        defaultIndex: expandAll ? [0] : undefined,
        width: '100%',
        background: 'surfacePrimaryFill'
      }}
      items={[
        {
          accordionItemProps: {
            marginBottom: 0.5
          },
          title: (
            <Flex flexDirection="row" gap={2} alignItems="center">
              <Checkbox
                ariaLabel="select-external-reviewer"
                id={email ?? reviewer.id}
                name="external-reviewer-info"
                isChecked={isSelected}
                onChange={() => onChange(reviewer)}
                disabled={disabled}
              >
                <Text textStyle="headingItem">{name || '--'}</Text>
              </Checkbox>
            </Flex>
          ),
          content: (
            <Flex flexDirection="column" gap={2}>
              <Flex justifyContent="flex-end">
                <ButtonText
                  size="regular"
                  iconLeft={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                  onClick={() => onViewDetails(reviewer)}
                >
                  View Details
                </ButtonText>
              </Flex>

              {affiliation && (
                <HStack align="start">
                  <Box mt={-0.5}>
                    <FontAwesomeIcon icon={faUniversity} />
                  </Box>
                  <Text textStyle="textContext">{affiliation}</Text>
                </HStack>
              )}

              {email && (
                <HStack align="start">
                  <Box mt={-0.3}>
                    <FontAwesomeIcon icon={faEnvelope} />
                  </Box>
                  <Text textStyle="textContext">{email}</Text>
                </HStack>
              )}

              {orcid && (
                <HStack align="start">
                  <Box mt={-0.5}>
                    <OrcidIDIcon value={orcid} />
                  </Box>
                  <Text textStyle="textContext">{orcid}</Text>
                </HStack>
              )}

              {(bibliometrics.length > 0 ||
                (score !== null && score !== undefined)) && (
                <Flex flexWrap="wrap" gap={2} alignItems="center">
                  {bibliometrics.length > 0 && (
                    <Text textStyle="textContext">
                      {bibliometrics.join(' · ')}
                    </Text>
                  )}
                  {score !== null && score !== undefined && (
                    <Flex gap={1} alignItems="center">
                      {bibliometrics.length > 0 && (
                        <Text textStyle="textContext">·</Text>
                      )}
                      <Text textStyle="textContext">Score</Text>
                      <Badge variant="success">{score.toFixed(2)}</Badge>
                    </Flex>
                  )}
                </Flex>
              )}

              {activeConflicts.length > 0 && (
                <Flex align="center" gap={2} mt={1} flexWrap="wrap">
                  <Text textStyle="textContext" fontWeight="600">
                    {providerName} Conflict Check:
                  </Text>
                  {activeConflicts.map(({ key, label }) => (
                    <Badge
                      key={key}
                      variant="caution"
                      whiteSpace="normal"
                      wordBreak="break-word"
                    >
                      <Text>{label}</Text>
                    </Badge>
                  ))}
                </Flex>
              )}

              {coi && (
                <Box mt={1}>
                  <ReviewerConflictInfo
                    errors={coi.error}
                    warnings={coi.warning}
                    info={coi.info}
                  />
                </Box>
              )}
            </Flex>
          )
        }
      ]}
    />
  );
};

export default ExternalRecommendationCard;
